import React, { useState, useCallback, useEffect } from 'react';
import useCalendarStore from '../../store/useCalendarStore';
import { toast } from 'sonner';
import {
  Cpu, Play, Loader2, Trophy, AlertTriangle, Zap,
  ArrowRightLeft, BarChart3, TrendingUp, CheckCircle2,
  Save
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';
import api from '../../api';

const METRIC_LABELS = {
  quality: { label: 'Quality Score', unit: '/100', icon: Trophy, color: 'text-amber-500' },
  utilization: { label: 'Faculty Utilization', unit: '%', icon: BarChart3, color: 'text-blue-500' },
  preference: { label: 'Preference Satisfaction', unit: '%', icon: CheckCircle2, color: 'text-emerald-500' },
  compactness: { label: 'Compactness Index', unit: '%', icon: TrendingUp, color: 'text-violet-500' },
  conflicts: { label: 'Conflicts', unit: '', icon: AlertTriangle, color: 'text-red-500', inverted: true },
  travel: { label: 'Travel Overhead', unit: '', icon: ArrowRightLeft, color: 'text-orange-500', inverted: true },
  coverage: { label: 'Coverage', unit: '%', icon: Zap, color: 'text-cyan-500' },
};

const ALGO_LABELS = {
  cpsat: { name: 'CP-SAT', desc: 'Constraint Solver', color: '#3B82F6', bg: 'bg-blue-500' },
  ga: { name: 'Genetic Algorithm', desc: 'Evolution Optimization', color: '#8B5CF6', bg: 'bg-violet-500' },
  sa: { name: 'Simulated Annealing', desc: 'Thermal Refinement', color: '#F59E0B', bg: 'bg-amber-500' },
};

function MetricBar({ value, maxValue = 100, color, inverted = false }) {
  const pct = Math.min(100, (value / maxValue) * 100);
  const displayPct = inverted ? Math.max(0, 100 - pct * 10) : pct;
  return (
    <div className="w-full h-2 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${displayPct}%`, background: color }} />
    </div>
  );
}

function GradeChip({ grade }) {
  const colors = {
    A: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400',
    B: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400',
    C: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400',
    D: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400',
    F: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
    'N/A': 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400',
  };
  return <Badge className={cn("text-sm font-bold px-3 py-1", colors[grade] || colors.F)}>{grade}</Badge>;
}

export default function AlgorithmDashboard() {
  const settings = useCalendarStore(s => s.settings);
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState({
    solver_timeout_seconds: settings?.solver_timeout_seconds ?? 30,
    scheduler_preference_weight: settings?.scheduler_preference_weight ?? 5,
    scheduler_morning_weight: settings?.scheduler_morning_weight ?? 3,
    scheduler_compactness_weight: settings?.scheduler_compactness_weight ?? -2,
    sa_cooling_rate: settings?.sa_cooling_rate ?? 0.995,
  });

  // Re-sync params when settings are loaded from the API
  useEffect(() => {
    if (!settings) return;
    setParams(p => ({
      ...p,
      solver_timeout_seconds: settings.solver_timeout_seconds ?? p.solver_timeout_seconds,
      scheduler_preference_weight: settings.scheduler_preference_weight ?? p.scheduler_preference_weight,
      scheduler_morning_weight: settings.scheduler_morning_weight ?? p.scheduler_morning_weight,
      scheduler_compactness_weight: settings.scheduler_compactness_weight ?? p.scheduler_compactness_weight,
      sa_cooling_rate: settings.sa_cooling_rate ?? p.sa_cooling_rate,
    }));
  }, [settings]);

  const updateParam = (key, value) => setParams(prev => ({ ...prev, [key]: value }));

  const runComparison = useCallback(async () => {
    setLoading(true);
    console.log('[AlgorithmDashboard] Running optimization engine...');
    
    // Legacy/Comparison endpoint for all algorithms (CP-SAT, GA, SA)
    const payload = { 
      date: targetDate, 
      ...params 
    };

    console.log('[AlgorithmDashboard] Endpoint:', api.defaults.baseURL + '/api/pipeline/compare-algorithms');
    console.log('[AlgorithmDashboard] Payload:', payload);
    
    try {
      const res = await api.post('/api/pipeline/compare-algorithms', payload, { timeout: 120000 });
      console.log('[AlgorithmDashboard] Success:', res.data);
      
      setComparison(res.data);
      toast.success('Algorithm comparison completed');
    } catch (err) {
      console.error('[AlgorithmDashboard] Error:', err);
      toast.error(err.response?.data?.detail || 'Optimization failed');
    }
    setLoading(false);
  }, [targetDate, params]);

  const saveAsTemplate = useCallback(async (algo, results) => {
    try {
      const payload = {
        name: `Optimized ${ALGO_LABELS[algo]?.name || algo} — ${targetDate}`,
        events: results.events,
        scope: { date: targetDate },
        is_starred: true
      };
      await api.post('/api/templates/promote', payload);
      toast.success('Saved as reusable template');
    } catch (err) {
      toast.error('Failed to save template');
    }
  }, [targetDate]);

  const previewOnCalendar = useCallback(async (algo, results) => {
    try {
      await api.post('/api/pipeline/preview-sandbox', {
        date: targetDate,
        events: results.events
      });
      toast.success('Solution loaded into Sandbox for preview');
      // No easy way to switch tabs from here without state lifting, 
      // but the toast informs the user.
    } catch (err) {
      toast.error('Failed to load preview');
    }
  }, [targetDate]);



  return (
    <div className="flex flex-col h-full bg-zinc-50/50 dark:bg-zinc-950/50" data-testid="algorithm-dashboard">
      {/* Header - More compact on mobile */}
      <div className="px-4 md:px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 sticky top-0 z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-zinc-800 dark:text-zinc-100 tracking-tight flex items-center gap-2">
              <Cpu className="w-5 h-5 text-blue-500" />
              Algorithm Comparison
            </h2>
            <p className="text-[10px] md:text-xs text-zinc-500 mt-0.5">Solve constraints via CP-SAT, GA, and SA pipelines</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input 
              type="date" 
              value={targetDate} 
              onChange={e => setTargetDate(e.target.value)} 
              className="w-full md:w-44 h-9 md:h-8 text-xs bg-zinc-50 dark:bg-zinc-950" 
              data-testid="algo-date-input" 
            />
            <Button 
              className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-white h-9 md:h-8 text-xs font-bold shadow-lg shadow-blue-500/20 active:scale-95 transition-all" 
              onClick={runComparison} 
              disabled={loading} 
              data-testid="run-comparison-btn"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
              Run Engine
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
        {/* Main Content Area */}
        <ScrollArea className="flex-1 h-full">
          <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
            {comparison?.status === 'no_data' && (
              <div className="flex flex-col items-center justify-center py-20 text-center animate-in fade-in zoom-in duration-300">
                <div className="w-16 h-16 rounded-3xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-zinc-800 dark:text-zinc-100 font-bold mb-1">No Source Data</h3>
                <p className="text-sm text-zinc-500 max-w-[280px]">Assignments or entity data missing for this period.</p>
              </div>
            )}

            {comparison?.comparison_matrix && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* Winner Card - Premium Glassmorphism */}
                <div className="relative p-6 rounded-2xl bg-gradient-to-br from-amber-400/10 via-amber-50 to-yellow-50 dark:from-amber-900/20 dark:via-zinc-900 dark:to-zinc-900 border border-amber-200 dark:border-amber-900/40 shadow-xl overflow-hidden group" data-testid="winner-banner">
                  <div className="absolute top-0 right-0 p-8 opacity-5 transition-transform group-hover:scale-110 duration-700">
                    <Trophy className="w-32 h-32 text-amber-500" />
                  </div>
                  <div className="relative flex items-center gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shadow-inner">
                      <Trophy className="w-6 h-6 md:w-7 md:h-7 text-amber-600 dark:text-amber-500" />
                    </div>
                    <div>
                      <p className="text-[10px] md:text-xs text-amber-600 dark:text-amber-400 font-black tracking-widest uppercase mb-1">Recommended Engine</p>
                      <h3 className="text-xl md:text-2xl font-black text-amber-900 dark:text-amber-300 items-baseline flex gap-2">
                        {ALGO_LABELS[comparison.recommendation]?.name || comparison.recommendation}
                        <span className="text-xs font-medium text-amber-600/60 dark:text-amber-500/40">Best overall performance</span>
                      </h3>
                    </div>
                    <div className="ml-auto hidden sm:block">
                      <GradeChip grade={comparison.comparison_matrix[comparison.recommendation]?.grade || 'F'} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {Object.entries(comparison.comparison_matrix).map(([algo, metrics], idx) => {
                    const info = ALGO_LABELS[algo] || { name: algo, color: '#666', bg: 'bg-zinc-500' };
                    const isWinner = algo === comparison.recommendation;
                    return (
                      <div 
                        key={algo} 
                        className={cn(
                          "data-card group relative p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1", 
                          isWinner && "ring-2 ring-amber-400 dark:ring-amber-900/60 shadow-lg"
                        )} 
                        style={{ animationDelay: `${idx * 100}ms` }}
                        data-testid={`algo-card-${algo}`}
                      >
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-3">
                            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-black shadow-lg", info.bg)}>
                              {algo === 'cpsat' ? 'CP' : algo.toUpperCase()}
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-zinc-800 dark:text-zinc-100 leading-tight">{info.name}</h4>
                              <p className="text-[10px] text-zinc-500 font-medium">{info.desc}</p>
                            </div>
                          </div>
                          <GradeChip grade={metrics.grade} />
                        </div>
                        
                        <div className="space-y-4">
                          {Object.entries(METRIC_LABELS).map(([key, meta]) => {
                            const val = metrics[key] ?? 0;
                            const Icon = meta.icon;
                            return (
                              <div key={key} className="relative">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[11px] font-bold text-zinc-500 flex items-center gap-2">
                                    <Icon className={cn("w-3.5 h-3.5", meta.color)} /> {meta.label}
                                  </span>
                                  <span className="text-xs font-black text-zinc-900 dark:text-zinc-100">{val}{meta.unit}</span>
                                </div>
                                <MetricBar value={val} color={info.color} inverted={meta.inverted} />
                              </div>
                            );
                          })}
                        </div>

                        <div className="mt-6 pt-4 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1 h-8 text-[10px] font-bold gap-1.5 hover:bg-indigo-500 hover:text-white transition-all group"
                            onClick={() => previewOnCalendar(algo, metrics)}
                          >
                            <Eye className="w-3 h-3 group-hover:scale-110" />
                            Preview
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1 h-8 text-[10px] font-bold gap-1.5 hover:bg-blue-500 hover:text-white transition-all group"
                            onClick={() => saveAsTemplate(algo, metrics)}
                          >
                            <Save className="w-3 h-3 group-hover:scale-110" />
                            Save as Template
                          </Button>
                        </div>
                      </div>

                    );
                  })}
                </div>
              </div>
            )}

            {!comparison && !loading && (
              <div className="flex flex-col items-center justify-center py-32 text-center opacity-40 hover:opacity-100 transition-opacity duration-700">
                <div className="w-20 h-20 rounded-full border-4 border-dashed border-zinc-200 dark:border-zinc-800 flex items-center justify-center mb-6">
                  <Cpu className="w-10 h-10 text-zinc-300 dark:text-zinc-700 animate-pulse" />
                </div>
                <h3 className="text-sm font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-[0.2em] mb-2">Algorithm Lab</h3>
                <p className="text-xs text-zinc-400 max-w-[240px]">Select a date above and initiate the comparison engine to begin refinement</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Parameter Sidebar - Optimized for responsiveness */}
        <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950/50 p-6 shadow-sm overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xs font-black text-zinc-900 dark:text-zinc-100 uppercase tracking-widest flex items-center gap-2">
              <Zap className="w-4 h-4 text-blue-500" /> 
              Engine Tunables
            </h3>
            <Badge variant="outline" className="text-[10px] font-bold bg-blue-50 dark:bg-blue-900/10 text-blue-600 border-blue-200 tabular-nums">LIVE</Badge>
          </div>
          
          <ScrollArea className="flex-1 lg:h-auto max-h-[400px] lg:max-h-none pr-1">
            <div className="space-y-8 pb-4">
                {[
                  { key: 'solver_timeout_seconds', label: 'Solver Timeout', min: 5, max: 120, step: 5, unit: 's', accent: 'accent-blue-600', color: 'text-blue-500', desc: 'Max search time for the constraint solver.' },
                  { key: 'scheduler_preference_weight', label: 'Faculty Preference', min: 0, max: 20, step: 1, unit: ' pts', accent: 'accent-emerald-600', color: 'text-emerald-500', desc: 'Reward for using faculty-preferred slots.' },
                  { key: 'scheduler_morning_weight', label: 'Morning Preference', min: 0, max: 10, step: 1, unit: ' pts', accent: 'accent-amber-600', color: 'text-amber-500', desc: 'Reward for scheduling classes earlier in the day.' },
                  { key: 'scheduler_compactness_weight', label: 'Compactness', min: -20, max: 0, step: 1, unit: ' pts', accent: 'accent-violet-600', color: 'text-violet-500', desc: 'Penalty per gap slot between batch classes (negative).' },
                ].map(p => (
                <div key={p.key} className="group">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">{p.label}</Label>
                    <span className={cn("text-xs font-black tabular-nums", p.color)}>{params[p.key] ?? 0}{p.unit}</span>
                  </div>
                  <input 
                    type="range" 
                    min={p.min} 
                    max={p.max} 
                    step={p.step} 
                    value={params[p.key] ?? (p.key === 'scheduler_compactness_weight' ? -2 : 0)}
                    onChange={e => updateParam(p.key, Number(e.target.value))}
                    className={cn("w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full appearance-none cursor-pointer transition-all focus:outline-none focus:ring-4 ring-blue-500/10", p.accent)}
                    data-testid={`slider-${p.key}`} 
                  />
                  <p className="text-[10px] text-zinc-400 font-medium mt-1.5 leading-tight opacity-0 group-hover:opacity-100 transition-opacity">{p.desc}</p>
                </div>
              ))}

              <div className="group pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Cooling Gradient</Label>
                  <span className="text-xs font-black text-amber-500 tabular-nums">{params.sa_cooling_rate}</span>
                </div>
                <input 
                  type="range" 
                  min={0.9} 
                  max={0.999} 
                  step={0.001} 
                  value={params.sa_cooling_rate}
                  onChange={e => updateParam('sa_cooling_rate', parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full appearance-none cursor-pointer transition-all focus:outline-none focus:ring-4 ring-amber-500/10 accent-amber-500"
                  data-testid="slider-sa_cooling_rate" 
                />
                <p className="text-[10px] text-zinc-400 font-medium mt-1.5 leading-tight opacity-0 group-hover:opacity-100 transition-opacity">Dynamic decay rate (λ)</p>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
